import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface EmailPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  logoUrl?: string;
  institutionName?: string;
  supportEmail?: string;
}

const theme = {
  colors: {
    brandPrimary: "#00b4c5",
    brandSecondary: "#6eb03f",
    textMain: "#2d3748",
    textLight: "#718096",
    bgLight: "#f4f7f9",
    white: "#ffffff",
    border: "#e2e8f0",
  },
  radius: "12px",
  fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
};

export function EmailPreviewDialog({
  open,
  onClose,
  logoUrl,
  institutionName = "Centro de Imágenes",
  supportEmail = "soporte@view-med.com",
}: EmailPreviewDialogProps) {
  const DEFAULT_LOGO = "https://viewmedonline.com/logo.png";
  const patientName = "Paciente Ejemplo";
  const studyName = "Rayos X de Tórax";
  const date = new Date().toLocaleDateString();

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl h-[90vh] p-0 flex flex-col">
        <DialogHeader className="p-6 border-b">
          <DialogTitle className="text-xl">Previsualización de Correo</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-auto bg-muted/30">
          <div
            style={{
              backgroundColor: theme.colors.bgLight,
              padding: "40px 20px",
              fontFamily: theme.fontFamily,
            }}
          >
            <div
              style={{
                backgroundColor: theme.colors.white,
                borderRadius: theme.radius,
                overflow: "hidden",
                border: `1px solid ${theme.colors.border}`,
                margin: "0 auto",
                width: "100%",
                maxWidth: "600px",
                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: "30px",
                  textAlign: "center",
                  backgroundColor: theme.colors.brandPrimary,
                }}
              >
                <img
                  src={logoUrl || DEFAULT_LOGO}
                  width="250"
                  alt="ViewMed"
                  style={{ margin: "0 auto", display: "block" }}
                />
              </div>

              {/* Content */}
              <div style={{ padding: "40px" }}>
                <div style={{ textAlign: "center", marginBottom: "30px" }}>
                  <img
                    src="http://cdn.mcauto-images-production.sendgrid.net/5152103177d67b6f/048c6f96-f6a3-4bb6-ad2f-e85abea626e4/990x660.png"
                    width="100%"
                    alt="Imaging Study"
                    style={{ borderRadius: "8px", maxWidth: "480px", height: "auto" }}
                  />
                </div>

                <h1
                  style={{
                    color: theme.colors.textMain,
                    fontSize: "22px",
                    fontWeight: "700",
                    textAlign: "center",
                    margin: "0 0 30px",
                    lineHeight: "1.2",
                  }}
                >
                  ¡Le han compartido un nuevo estudio de imagenología!
                </h1>

                {/* Info Card */}
                <div
                  style={{
                    backgroundColor: "#f0f9fa",
                    padding: "24px",
                    borderRadius: "8px",
                    marginBottom: "30px",
                    border: "1px solid #e0f2f3",
                  }}
                >
                  <p
                    style={{
                      margin: "0",
                      color: theme.colors.textLight,
                      fontSize: "10px",
                      fontWeight: "bold",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    PACIENTE
                  </p>
                  <p
                    style={{
                      margin: "4px 0 12px",
                      color: theme.colors.textMain,
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  >
                    {patientName}
                  </p>
                  <hr
                    style={{
                      border: "none",
                      borderTop: "1px solid #e0f2f3",
                      margin: "0 0 12px",
                    }}
                  />

                  <p
                    style={{
                      margin: "0",
                      color: theme.colors.textLight,
                      fontSize: "10px",
                      fontWeight: "bold",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    ESTUDIO
                  </p>
                  <p
                    style={{
                      margin: "4px 0 12px",
                      color: theme.colors.textMain,
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  >
                    {studyName}
                  </p>
                  <hr
                    style={{
                      border: "none",
                      borderTop: "1px solid #e0f2f3",
                      margin: "0 0 12px",
                    }}
                  />

                  <p
                    style={{
                      margin: "0",
                      color: theme.colors.textLight,
                      fontSize: "10px",
                      fontWeight: "bold",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    FECHA
                  </p>
                  <p
                    style={{
                      margin: "4px 0 12px",
                      color: theme.colors.textMain,
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  >
                    {date}
                  </p>
                  <hr
                    style={{
                      border: "none",
                      borderTop: "1px solid #e0f2f3",
                      margin: "0 0 12px",
                    }}
                  />

                  <p
                    style={{
                      margin: "0",
                      color: theme.colors.textLight,
                      fontSize: "10px",
                      fontWeight: "bold",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    COMPARTIDO POR
                  </p>
                  <p
                    style={{
                      margin: "4px 0 12px",
                      color: theme.colors.textMain,
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  >
                    {institutionName}
                  </p>
                </div>

                {/* Button */}
                <div style={{ textAlign: "center", marginBottom: "30px" }}>
                  <div
                    style={{
                      backgroundColor: theme.colors.brandPrimary,
                      color: "#ffffff",
                      padding: "16px 32px",
                      borderRadius: "6px",
                      fontSize: "16px",
                      fontWeight: "600",
                      textDecoration: "none",
                      display: "inline-block",
                    }}
                  >
                    Ver Imágenes
                  </div>
                </div>

                <p
                  style={{
                    color: theme.colors.textLight,
                    fontSize: "13px",
                    lineHeight: "20px",
                    textAlign: "center",
                    margin: "0",
                  }}
                >
                  Si tiene algún problema para acceder, contacte a soporte en{" "}
                  <span
                    style={{
                      color: theme.colors.brandPrimary,
                      textDecoration: "underline",
                      fontWeight: "500",
                    }}
                  >
                    {supportEmail}
                  </span>
                </p>
              </div>

              {/* Footer */}
              <div
                style={{
                  padding: "20px",
                  backgroundColor: "#f9fafb",
                  textAlign: "center",
                  borderTop: `1px solid ${theme.colors.border}`,
                }}
              >
                <p
                  style={{
                    color: theme.colors.textLight,
                    fontSize: "13px",
                    marginBottom: "8px",
                    marginTop: "0",
                  }}
                >
                  Plataforma tecnológica por{" "}
                  <span style={{ color: theme.colors.brandPrimary, fontWeight: "bold" }}>
                    ViewMed
                  </span>
                </p>
                <p
                  style={{ color: theme.colors.textLight, fontSize: "11px", margin: "0" }}
                >
                  © 2026 Viewmed. Todos los derechos reservados.
                </p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
